import { FC } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { Button } from '@mui/material';

import { MyToverAPI } from 'api/MyToverAPI';
import { useAuth } from 'contexts/AuthContext';
import { AccountBanner } from 'components/account/AccountBanner';

/**
 * Shown for as long as support is looking at someone else's My Tover. It has to be
 * unmissable: everything below it is another person's account, and support must never take
 * what they see here for their own. Leaving is one click, and the view lapses by itself.
 */
export const ViewAsBanner: FC = () => {
  const { t } = useTranslation();
  const auth = useAuth();
  const viewedAs = auth.state.account?.viewed_as;

  if (!viewedAs) return null;

  const stopViewing = async () => {
    await MyToverAPI.stopViewingAsUser();
    // Same reasoning as starting: leave the customer's screens by booting fresh, so nothing
    // still on screen refetches with the identity that just changed.
    window.location.assign('/manage/users');
  };

  return (
    <AccountBanner
      id="view-as-banner"
      severity="error"
      variant="filled"
      fixed
      // The colour belongs on the full-width strip, not on the centred content inside it, or
      // the alert's red stops short of the window edges.
      sx={{ backgroundColor: '#c62828' }}
      alertSx={{
        'paddingInline': 2,
        'backgroundColor': 'transparent',
        'color': 'common.white',
        'fontWeight': 'fontWeightMedium',
        'borderRadius': 0,
        // The name is the one thing that must be read at a glance, so it is set apart from
        // the sentence around it rather than blending into the red.
        '& #view-as-name': {
          fontWeight: 'fontWeightBold',
          textDecoration: 'underline',
          textUnderlineOffset: 3,
        },
      }}
      action={
        <Button
          id="view-as-stop"
          color="inherit"
          size="small"
          variant="outlined"
          onClick={stopViewing}
          sx={{ borderColor: 'common.white' }}
        >
          {t('view-as.banner.stop')}
        </Button>
      }
    >
      {/* One sentence for translators, with the name interpolated into it. */}
      <Trans
        i18nKey="view-as.banner.viewing"
        values={{ name: viewedAs.name }}
        components={[<span id="view-as-name" />]}
      />
    </AccountBanner>
  );
};
